import api from "./api";

const ACCEPTED_KEY = "push-notification-accepted";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const getToken = () => {
  try {
    return localStorage.getItem("token");
  } catch (error) {
    return null;
  }
};

/** Ative logs detalhados no console: localStorage.setItem('LOG_PUSH','1') e recarregue */
const pushVerbose = () => {
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem("LOG_PUSH") === "1";
  } catch {
    return false;
  }
};

const logPush = (msg, detail) => {
  if (detail !== undefined) {
    console.info("[Push]", msg, detail);
  } else {
    console.info("[Push]", msg);
  }
};

/**
 * Registra Web Push no navegador e grava subscription no backend (usuario logado).
 * @returns {{ ok: boolean, message?: string }} message preenchida quando ok=false (para toast)
 */
export const registerPushNotifications = async () => {
  const fail = (message) => {
    logPush(message);
    return { ok: false, message };
  };

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    return fail(
      "Este navegador não suporta push ou falta HTTPS — use Chrome/Edge ou instale a PWA."
    );
  }

  const token = getToken();
  if (!token) {
    return fail("Faça login antes de ativar notificações.");
  }

  const permission = Notification.permission;
  if (permission === "denied") {
    return fail(
      "Notificações bloqueadas para este site — ative nas configurações do navegador (cadeado/ícone do site)."
    );
  }
  // Importante: só chamamos isso quando o usuário clicou/consentiu.
  // Então aqui solicitamos permissão diretamente.
  if (permission === "default") {
    const permissionResult = await Notification.requestPermission();
    if (permissionResult !== "granted") {
      return fail(
        permissionResult === "denied"
          ? "Permissão negada. Libere notificações nas configurações do navegador."
          : "Permissão de notificação não concedida."
      );
    }
  }

  if (pushVerbose()) {
    logPush("Fluxo iniciado", {
      apiBase: process.env.REACT_APP_BACKEND_URL || "(REACT_APP_BACKEND_URL não definido)"
    });
  }

  localStorage.setItem(ACCEPTED_KEY, "true");

  let publicKey;
  try {
    const { data } = await api.get("/push/public-key");
    publicKey = data?.publicKey;
  } catch (err) {
    console.warn("[Push] Falha ao obter VAPID público do backend:", err?.response?.status || err?.message || err);
    return fail(
      err?.response?.status
        ? `Servidor não respondeu a chave VAPID (HTTP ${err.response.status}). Verifique o backend e REACT_APP_BACKEND_URL.`
        : "Não foi possível obter a chave VAPID do servidor. Verifique a URL da API."
    );
  }
  if (!publicKey) {
    console.warn("[Push] Backend sem VAPID_PUBLIC_KEY / WEB_PUSH_VAPID_PUBLIC_KEY — configure o .env e reinicie.");
    return fail(
      "Backend sem chaves VAPID — defina VAPID_PUBLIC_KEY no servidor (.env) e reinicie."
    );
  }

  let registration;
  try {
    registration = await navigator.serviceWorker.ready;
  } catch (err) {
    console.warn("[Push] Service worker não está pronto. Confira se /service-worker.js está acessível (HTTPS/PWA).", err);
    return fail(
      "Service worker não está ativo — use HTTPS, verifique se /service-worker.js carrega e recarregue a página."
    );
  }
  let subscription;
  try {
    subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });
    }
  } catch (err) {
    console.warn(
      "[Push] Falha ao assinar push no navegador (VAPID incorreto ou permissão):",
      err?.message || err
    );
    return fail(
      "Falha ao registrar push no navegador — confira se a chave VAPID no servidor coincide com a do cliente."
    );
  }

  try {
    await api.post(
      "/push/subscribe",
      {
        endpoint: subscription.endpoint,
        keys: subscription.toJSON().keys,
        userAgent: navigator.userAgent,
        device: /iPhone|iPad|iPod/i.test(navigator.userAgent)
          ? "ios"
          : /Android/i.test(navigator.userAgent)
            ? "android"
            : "web"
      },
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );
  } catch (err) {
    console.warn("[Push] Falha ao registrar subscription no servidor:", err?.response?.status || err?.message || err);
    const status = err?.response?.status;
    return fail(
      status === 401 || status === 403
        ? "Sessão inválida ou sem permissão — faça login de novo e tente ativar notificações."
        : status
          ? `Erro ao salvar no servidor (HTTP ${status}). Tente de novo ou contate o suporte.`
          : "Erro de rede ao salvar a subscription. Verifique sua conexão e a URL do backend."
    );
  }

  logPush("Registro concluído: subscription enviada ao servidor; aguarde mensagens/tickets para testar.");
  return { ok: true };
};
